/**
 * Root Cause Analysis Module Configuration
 *
 * Deep investigation tools including SHAP analysis, correlations,
 * historical pattern matching, and root cause library.
 */

import { ModuleConfig } from './moduleTypes'

export const rootCauseConfig: ModuleConfig = {
  id: 'root-cause',
  name: 'Root Cause Explorer',
  description: 'Deep investigation and root cause analysis',
  icon: 'Microscope',
  basePath: '/root-cause',
  enabledIndustries: ['manufacturing', 'oil_gas', 'healthcare', 'financial', 'energy', 'transportation', 'mining', 'chemicals', 'food_beverage', 'pharmaceuticals', 'aerospace', 'metals', 'iot_telecom', 'utilities', 'pulp_paper', 'automotive', 'datacenter', 'facility_management',
    // Phase 4 — non-industrial + medical-grade verticals (2026-06-27)
    'aquaculture_lobster', 'aquaculture_salmon', 'aquaculture_shrimp',
    'biosensor_ide_wearable', 'biosensor_continuous_glucose', 'biosensor_oximeter_fleet',
    'wearable_etd'],

  subPages: [
    {
      id: 'overview',
      path: '',
      name: 'Analysis Overview',
      icon: 'LayoutDashboard',
      description: 'Overview of recent analyses and quick access',
      component: 'RootCauseOverview',
      requiredPermissions: ['analytics:read'],
      position: 1,
      industryLabels: {
        healthcare: 'Diagnostic Overview',
        financial: 'Incident Overview',
        iot_telecom: 'Connectivity Issue Overview',
      },
    },
    {
      id: 'asset-rca',
      path: ':assetId',
      name: 'Asset Analysis',
      icon: 'Search',
      description: 'Root cause analysis for specific asset',
      component: 'AssetRootCause',
      requiredPermissions: ['analytics:read'],
      position: 2,
      requiresAssetId: true,
      industryLabels: {
        healthcare: 'Device Diagnostics',
        financial: 'System Analysis',
        transportation: 'Vehicle Analysis',
        iot_telecom: 'SIM Diagnostics',
      },
    },
    {
      id: 'shap',
      path: ':assetId/shap',
      name: 'SHAP Analysis',
      icon: 'BarChart2',
      description: 'Feature importance and SHAP explanations',
      component: 'SHAPExplanation',
      requiredPermissions: ['analytics:read'],
      position: 3,
      requiresAssetId: true,
    },
    {
      id: 'correlations',
      path: ':assetId/correlations',
      name: 'Sensor Correlations',
      icon: 'Network',
      description: 'Sensor correlation matrix and relationships',
      component: 'CorrelationMatrix',
      requiredPermissions: ['analytics:read'],
      position: 4,
      requiresAssetId: true,
      industryLabels: {
        healthcare: 'Parameter Correlations',
        financial: 'Metric Correlations',
        transportation: 'Telematics Correlations',
        iot_telecom: 'Signal Correlations',
      },
    },
    {
      id: 'patterns',
      path: ':assetId/patterns',
      name: 'Historical Patterns',
      icon: 'History',
      description: 'Pattern matching against historical events',
      component: 'PatternMatching',
      requiredPermissions: ['analytics:read'],
      position: 5,
      requiresAssetId: true,
    },
    {
      id: 'library',
      path: 'library',
      name: 'Root Cause Library',
      icon: 'BookOpen',
      description: 'Knowledge base of known root causes',
      component: 'RootCauseLibraryView',
      requiredPermissions: ['knowledge:read'],
      position: 6,
      industryLabels: {
        healthcare: 'Diagnostic Library',
        financial: 'Incident Library',
        transportation: 'Failure Mode Library',
        iot_telecom: 'Connectivity Issue Library',
      },
    },
    {
      id: 'report',
      path: 'report',
      name: 'RCA Report',
      icon: 'FileText',
      description: 'Generate root cause analysis reports',
      component: 'RCAReportGenerator',
      requiredPermissions: ['reports:write'],
      position: 7,
      industryLabels: {
        healthcare: 'Diagnostic Report',
        financial: 'Incident Report',
        iot_telecom: 'Connectivity Analysis Report',
      },
    },
  ],

  workflows: [
    {
      id: 'full-rca',
      name: 'Full Root Cause Analysis',
      description: 'Comprehensive root cause investigation',
      icon: 'Search',
      steps: [
        {
          id: 'select-asset',
          name: 'Select Asset',
          description: 'Choose the asset to investigate',
          navigateTo: 'overview',
          icon: 'Target',
          estimatedDuration: 2,
        },
        {
          id: 'review-shap',
          name: 'Review Feature Importance',
          description: 'Analyze SHAP values for key contributors',
          navigateTo: 'shap',
          icon: 'BarChart2',
          estimatedDuration: 10,
        },
        {
          id: 'check-correlations',
          name: 'Check Correlations',
          description: 'Identify correlated sensors and cascading effects',
          navigateTo: 'correlations',
          icon: 'Network',
          estimatedDuration: 10,
        },
        {
          id: 'match-patterns',
          name: 'Match Historical Patterns',
          description: 'Compare to similar past events',
          navigateTo: 'patterns',
          icon: 'History',
          estimatedDuration: 10,
        },
        {
          id: 'consult-library',
          name: 'Consult Root Cause Library',
          description: 'Reference known root causes',
          navigateTo: 'library',
          icon: 'BookOpen',
          estimatedDuration: 5,
        },
        {
          id: 'generate-report',
          name: 'Generate Report',
          description: 'Document findings and recommendations',
          navigateTo: 'report',
          icon: 'FileText',
          estimatedDuration: 10,
        },
      ],
      triggerConditions: ['anomalyDetected', 'failureOccurred', 'investigationRequested'],
    },
    {
      id: 'quick-diagnosis',
      name: 'Quick Diagnosis',
      description: 'Rapid root cause identification',
      icon: 'Zap',
      steps: [
        {
          id: 'review-shap-quick',
          name: 'Review Top Contributors',
          description: 'Quickly identify main contributing factors',
          navigateTo: 'shap',
          icon: 'BarChart2',
          estimatedDuration: 5,
        },
        {
          id: 'check-library',
          name: 'Check Root Cause Library',
          description: 'Match symptoms to known causes',
          navigateTo: 'library',
          icon: 'BookOpen',
          estimatedDuration: 5,
        },
        {
          id: 'recommend-action',
          name: 'Recommend Action',
          description: 'Suggest immediate corrective action',
          icon: 'Lightbulb',
          estimatedDuration: 3,
        },
      ],
      triggerConditions: ['criticalAlert', 'urgentInvestigation'],
    },
  ],

  narratives: {
    sections: [
      {
        id: 'anomaly-causation',
        name: 'Anomaly Causation Chain',
        template: 'Root cause analysis for {assetName}. Top 3 contributing factors: 1) {factor1} ({factor1Percent}%), 2) {factor2} ({factor2Percent}%), 3) {factor3} ({factor3Percent}%).',
        order: 1,
        required: true,
      },
      {
        id: 'technical-analysis',
        name: 'Technical Analysis',
        template: '{technicalExplanation}',
        order: 2,
      },
      {
        id: 'correlations',
        name: 'Sensor Correlations',
        template: 'High correlations detected: {correlationPairs}. This suggests: {correlationInterpretation}.',
        order: 3,
        condition: 'hasHighCorrelations',
      },
      {
        id: 'historical-match',
        name: 'Historical Pattern Analysis',
        template: 'Pattern matches {matchCount} historical events with {avgSimilarity}% average similarity. Most similar: {mostSimilarEvent} ({mostSimilarPercent}% match).',
        order: 4,
        condition: 'hasHistoricalMatches',
      },
      {
        id: 'root-cause',
        name: 'Identified Root Cause',
        template: '**Most Likely Root Cause:** {rootCauseName}\n**Confidence:** {rootCauseConfidence}%\n**Category:** {rootCauseCategory}',
        order: 5,
        required: true,
      },
      {
        id: 'recommendations',
        name: 'Recommendations',
        template: '**Recommended Actions:**\n1. {action1} ({action1Priority})\n2. {action2} ({action2Priority})\n3. {action3} ({action3Priority})',
        order: 6,
        required: true,
      },
    ],
    availableVariables: [
      'assetName', 'factor1', 'factor1Percent', 'factor2', 'factor2Percent', 'factor3', 'factor3Percent',
      'technicalExplanation', 'correlationPairs', 'correlationInterpretation',
      'matchCount', 'avgSimilarity', 'mostSimilarEvent', 'mostSimilarPercent',
      'rootCauseName', 'rootCauseConfidence', 'rootCauseCategory',
      'action1', 'action1Priority', 'action2', 'action2Priority', 'action3', 'action3Priority',
    ],
  },

  industryOverrides: {
    healthcare: {
      name: 'Diagnostic Analysis',
      description: 'Medical device diagnostic investigation',
      icon: 'Stethoscope',
      additionalSubPages: [
        {
          id: 'clinical-context',
          path: ':assetId/clinical',
          name: 'Clinical Context',
          icon: 'FileHeart',
          description: 'Clinical usage context and impact',
          component: 'ClinicalContext',
          position: 8,
          requiresAssetId: true,
        },
      ],
    },
    financial: {
      name: 'Incident Analysis',
      description: 'System incident investigation and root cause',
      icon: 'AlertCircle',
      additionalSubPages: [
        {
          id: 'impact-analysis',
          path: ':assetId/impact',
          name: 'Business Impact',
          icon: 'TrendingDown',
          description: 'Business impact and downstream effects',
          component: 'BusinessImpactAnalysis',
          position: 8,
          requiresAssetId: true,
        },
      ],
    },
    transportation: {
      name: 'Failure Analysis',
      description: 'Vehicle failure investigation',
      additionalSubPages: [
        {
          id: 'driver-context',
          path: ':assetId/driver',
          name: 'Driver Context',
          icon: 'User',
          description: 'Driver behavior context during incident',
          component: 'DriverContext',
          position: 8,
          requiresAssetId: true,
        },
      ],
    },
    iot_telecom: {
      name: 'Connectivity Diagnostics',
      description: 'SIM connectivity troubleshooting with Multi-IMSI analysis',
      icon: 'Search',
      additionalSubPages: [
        {
          id: 'signal-diagnostics',
          path: ':assetId/signal-diag',
          name: 'Signal Diagnostics',
          icon: 'Signal',
          description: 'Deep dive into signal degradation root causes',
          component: 'SignalDiagnostics',
          position: 8,
          requiresAssetId: true,
        },
        {
          id: 'carrier-analysis',
          path: ':assetId/carrier-analysis',
          name: 'Carrier Analysis',
          icon: 'Radio',
          description: 'Analyze carrier-specific issues and handover failures',
          component: 'CarrierAnalysis',
          position: 9,
          requiresAssetId: true,
        },
        {
          id: 'profile-diagnostics',
          path: ':assetId/profile-diag',
          name: 'Profile Diagnostics',
          icon: 'Layers',
          description: 'Diagnose eUICC profile and Multi-IMSI configuration issues',
          component: 'ProfileDiagnostics',
          position: 10,
          requiresAssetId: true,
        },
        {
          id: 'network-timeline',
          path: ':assetId/network-timeline',
          name: 'Network Event Timeline',
          icon: 'Clock',
          description: 'Timeline of network events leading to connectivity issues',
          component: 'NetworkEventTimeline',
          position: 11,
          requiresAssetId: true,
        },
        {
          id: 'geographic-analysis',
          path: ':assetId/geo-analysis',
          name: 'Geographic Analysis',
          icon: 'Map',
          description: 'Location-based connectivity issue analysis',
          component: 'GeographicAnalysis',
          position: 12,
          requiresAssetId: true,
        },
        {
          id: 'connectivity-library',
          path: 'connectivity-library',
          name: 'Connectivity Issue Library',
          icon: 'BookOpen',
          description: 'Knowledge base of common IoT connectivity issues',
          component: 'ConnectivityIssueLibrary',
          position: 13,
        },
      ],
      additionalWorkflows: [
        {
          id: 'connectivity-troubleshooting',
          name: 'Connectivity Troubleshooting',
          description: 'Systematic diagnosis of SIM connectivity issues',
          icon: 'Search',
          steps: [
            {
              id: 'check-signal',
              name: 'Check Signal Diagnostics',
              description: 'Analyze signal quality metrics',
              navigateTo: 'signal-diagnostics',
              icon: 'Signal',
              estimatedDuration: 5,
            },
            {
              id: 'review-carrier',
              name: 'Review Carrier Status',
              description: 'Check carrier-specific issues',
              navigateTo: 'carrier-analysis',
              icon: 'Radio',
              estimatedDuration: 5,
            },
            {
              id: 'check-profiles',
              name: 'Check Profile Configuration',
              description: 'Verify eUICC and Multi-IMSI settings',
              navigateTo: 'profile-diagnostics',
              icon: 'Layers',
              estimatedDuration: 5,
            },
            {
              id: 'review-timeline',
              name: 'Review Event Timeline',
              description: 'Trace network events chronologically',
              navigateTo: 'network-timeline',
              icon: 'Clock',
              estimatedDuration: 5,
            },
            {
              id: 'check-location',
              name: 'Check Geographic Factors',
              description: 'Analyze location-based issues',
              navigateTo: 'geographic-analysis',
              icon: 'Map',
              estimatedDuration: 5,
            },
            {
              id: 'consult-library',
              name: 'Consult Issue Library',
              description: 'Match symptoms to known issues',
              navigateTo: 'connectivity-library',
              icon: 'BookOpen',
              estimatedDuration: 3,
            },
          ],
          triggerConditions: ['connectivityLoss', 'signalDrop', 'highLatency', 'frequentDisconnects'],
        },
        {
          id: 'multi-imsi-diagnosis',
          name: 'Multi-IMSI Diagnosis',
          description: 'Diagnose issues with Multi-IMSI profile switching',
          icon: 'Layers',
          steps: [
            {
              id: 'check-profiles',
              name: 'Check Profile Status',
              description: 'Verify all eUICC profiles are active',
              navigateTo: 'profile-diagnostics',
              icon: 'Layers',
              estimatedDuration: 3,
            },
            {
              id: 'compare-carriers',
              name: 'Compare Carrier Performance',
              description: 'Analyze performance across profiles',
              navigateTo: 'carrier-analysis',
              icon: 'Radio',
              estimatedDuration: 5,
            },
            {
              id: 'review-shap',
              name: 'Review Factor Analysis',
              description: 'Identify contributing factors using SHAP',
              navigateTo: 'shap',
              icon: 'BarChart2',
              estimatedDuration: 5,
            },
            {
              id: 'generate-recommendation',
              name: 'Generate Recommendation',
              description: 'AI-powered profile switch recommendation',
              icon: 'Lightbulb',
              estimatedDuration: 3,
            },
          ],
          triggerConditions: ['profileSwitchFailed', 'suboptimalCarrier', 'roamingIssues'],
        },
      ],
    },
    datacenter: {
      name: 'Outage Analysis',
      description: 'Server and infrastructure failure investigation',
      icon: 'Server',
    },
  },

  requiredPermissions: ['analytics:read'],
}
